import { createFileRoute, useSearch } from "@tanstack/react-router";
import { useCallback, useEffect, useState } from "react";
import { ArrowLeft, Copy, Database, Loader2, Pencil } from "lucide-react";
import { Link, useNavigate } from "@tanstack/react-router";
import { toast } from "sonner";

import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import { ScrollableTable } from "@/components/ui/scrollable-table";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";

import { usePortalData } from "@/components/portal/PortalDataProvider";
import { useAuth } from "@/contexts/AuthContext";
import { usePermissions } from "@/hooks/usePermissions";
import { portalFetch } from "@/lib/portalApi";
import type { DatasourceDefinition } from "@/components/portal/apps/reporting/datasources/types";
import { DatasourceQueryPreview } from "@/components/portal/apps/reporting/datasources/DatasourceQueryPreview";
import { DatasourceLinkedReports } from "@/components/portal/apps/reporting/datasources/DatasourceLinkedReports";
import { DatasourceAuditTab } from "@/components/portal/apps/reporting/datasources/DatasourceAuditTab";
import { DatasourceVariantsList } from "@/components/portal/apps/reporting/datasources/DatasourceVariantsList";
import { measureKind } from "@/components/portal/apps/reporting/datasources/measureKind";
import { parseColumnFilterSearch, searchAsStrings } from "@/components/portal/apps/reporting/custom/columnFilterParams";
import { FiltersNotAppliedNotice } from "@/components/portal/apps/reporting/datasources/FiltersNotAppliedNotice";

/**
 * Open string map, like `custom/$slug.tsx`: a Space tile's "Open datasource
 * (admin)" link and the drill-through drawer footer carry the clicked slice as
 * `cf.{column}.{op}` keys (one composer, web #1192). This admin page does not
 * apply them: its preview runs dp `test-query`, which takes placeholder params
 * only. It names them instead (chart-interactions-seam D7).
 */
type DatasourceSearch = Record<string, string>;

export const Route = createFileRoute(
  "/portal/apps/reporting/datasources/$slug",
)({
  component: ViewDatasourcePage,
  validateSearch: (search: Record<string, unknown>): DatasourceSearch => searchAsStrings(search),
});

function ViewDatasourcePage() {
  const { slug } = Route.useParams();
  const search = useSearch({ from: "/portal/apps/reporting/datasources/$slug" });
  const sliceFilters = parseColumnFilterSearch(search);
  const { selectedOrganisation } = usePortalData();
  const organisationId = selectedOrganisation?.organisationId ?? "";
  const { token } = useAuth();
  const { can, bypassesGroups } = usePermissions();
  const navigate = useNavigate();
  const canManage = can("manage", "reporting");

  const [datasource, setDatasource] = useState<DatasourceDefinition | null>(
    null,
  );
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [cloning, setCloning] = useState(false);
  const [linkedReportCount, setLinkedReportCount] = useState<number | null>(
    null,
  );

  const handleCountChange = useCallback((count: number) => {
    setLinkedReportCount(count);
  }, []);

  const [variantCount, setVariantCount] = useState<number | null>(null);
  const handleVariantCountChange = useCallback((count: number) => {
    setVariantCount(count);
  }, []);

  const handleClone = async () => {
    if (!datasource) return;
    setCloning(true);
    try {
      const created = await portalFetch<DatasourceDefinition>(
        `/api/v1/gateway/reporting/api/v1/datasources/${datasource.slug}/clone`,
        { method: "POST", token: token ?? undefined },
      );
      toast.success(`Cloned as "${created.name}"`);
      navigate({
        to: "/portal/apps/reporting/datasources/$slug/edit",
        params: { slug: created.slug },
      });
    } catch (err) {
      toast.error(
        err instanceof Error ? err.message : "Failed to clone datasource",
      );
    } finally {
      setCloning(false);
    }
  };

  useEffect(() => {
    if (!organisationId || !slug) return;

    setLoading(true);
    portalFetch<DatasourceDefinition>(
      `/api/v1/gateway/reporting/api/v1/datasources/${slug}`,
      { token: token ?? undefined },
    )
      .then(setDatasource)
      .catch((err) =>
        setError(err instanceof Error ? err.message : "Failed to load"),
      )
      .finally(() => setLoading(false));
  }, [organisationId, slug, token]);

  if (loading) {
    return (
      <div className="flex items-center justify-center py-24">
        <Loader2 className="h-8 w-8 animate-spin text-muted-foreground" />
      </div>
    );
  }

  if (error || !datasource) {
    return (
      <div className="text-center py-12">
        <p className="text-destructive">{error || "Datasource not found"}</p>
        <Button
          variant="outline"
          className="mt-4"
          onClick={() =>
            navigate({ to: "/portal/apps/reporting/datasources" })
          }
        >
          Back to datasources
        </Button>
      </div>
    );
  }

  return (
    <div className="space-y-8 max-w-5xl mx-auto">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-4">
          <Button
            variant="ghost"
            size="icon"
            onClick={() =>
              navigate({ to: "/portal/apps/reporting/datasources" })
            }
          >
            <ArrowLeft className="h-4 w-4" />
          </Button>
          <div>
            <div className="flex items-center gap-2">
              <Database className="h-5 w-5 text-muted-foreground" />
              <h2 className="text-2xl font-semibold">{datasource.name}</h2>
              {datasource.isSystem && (
                <Badge variant="secondary">System</Badge>
              )}
              {!datasource.isActive && (
                <Badge variant="destructive">Inactive</Badge>
              )}
            </div>
            {datasource.description && (
              <p className="text-sm text-muted-foreground mt-1">
                {datasource.description}
              </p>
            )}
          </div>
        </div>
        {canManage && (
          <div className="flex items-center gap-2">
            <Button
              variant="outline"
              onClick={handleClone}
              disabled={cloning}
            >
              {cloning ? (
                <Loader2 className="h-4 w-4 mr-2 animate-spin" />
              ) : (
                <Copy className="h-4 w-4 mr-2" />
              )}
              Clone
            </Button>
            {!datasource.isSystem && (
              <Button asChild>
                <Link
                  to="/portal/apps/reporting/datasources/$slug/edit"
                  params={{ slug }}
                >
                  <Pencil className="h-4 w-4 mr-2" />
                  Edit
                </Link>
              </Button>
            )}
          </div>
        )}
      </div>

      {/* Tabs: Overview / Reports / Variants. Variants is hidden for system
          datasources — the backend rejects writes with 400 and there's no
          read-only variant story for system datasources in v1. */}
      <Tabs defaultValue="overview">
        <TabsList>
          <TabsTrigger value="overview">Overview</TabsTrigger>
          <TabsTrigger value="reports" className="gap-2">
            Reports
            {linkedReportCount !== null && (
              <Badge variant="secondary" className="text-xs">
                {linkedReportCount}
              </Badge>
            )}
          </TabsTrigger>
          {!datasource.isSystem && (
            <TabsTrigger value="variants" className="gap-2">
              Variants
              {variantCount !== null && (
                <Badge variant="secondary" className="text-xs">
                  {variantCount}
                </Badge>
              )}
            </TabsTrigger>
          )}
          {/* Audit tab — admin-only. Hidden for system datasources (no
              variants there) and for non-admins (DP rejects the
              compare-variants call with 403 anyway). */}
          {!datasource.isSystem && bypassesGroups && (
            <TabsTrigger value="audit">Audit</TabsTrigger>
          )}
        </TabsList>

        <TabsContent value="overview" className="space-y-8 mt-6">
          {/* Query */}
          <section className="rounded-lg border p-6 space-y-3">
            <h3 className="text-lg font-medium">Query</h3>
            <pre className="text-sm font-mono bg-muted/50 rounded-lg p-4 overflow-x-auto whitespace-pre-wrap">
              {datasource.query}
            </pre>
          </section>

          {/* Preview — parameter inputs + sample data + measure totals. */}
          <FiltersNotAppliedNotice filters={sliceFilters} />
          <section className="rounded-lg border p-6">
            <DatasourceQueryPreview
              query={datasource.query}
              measures={datasource.measures ?? undefined}
              dateRoles={datasource.dateRoles ?? undefined}
              verbose
            />
          </section>

          {/* Detected columns */}
          {datasource.columns && datasource.columns.length > 0 && (
            <section className="rounded-lg border p-6 space-y-3">
              <h3 className="text-lg font-medium">
                Columns{" "}
                <Badge variant="secondary" className="ml-2">
                  {datasource.columns.length}
                </Badge>
              </h3>
              <ScrollableTable>
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead>Column</TableHead>
                    <TableHead>Label</TableHead>
                    <TableHead>SQL Type</TableHead>
                    <TableHead>Display Type</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {datasource.columns.map((col) => (
                    <TableRow key={col.key}>
                      <TableCell className="font-mono text-sm">{col.key}</TableCell>
                      <TableCell>{col.label}</TableCell>
                      <TableCell>
                        <Badge variant="outline">{col.sqlType}</Badge>
                      </TableCell>
                      <TableCell>
                        <Badge variant="secondary">{col.displayType}</Badge>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
              </ScrollableTable>
            </section>
          )}

          {/* Measures — datasource-level pool inherited by every report and
              dashboard tile built on this datasource. Read-only view: edit
              via the Edit page. Render even when empty so admins know the
              feature exists and where to find it. */}
          <section className="rounded-lg border p-6 space-y-3">
            <h3 className="text-lg font-medium">
              Measures{" "}
              <Badge variant="secondary" className="ml-2">
                {datasource.measures?.length ?? 0}
              </Badge>
            </h3>
            {datasource.measures && datasource.measures.length > 0 ? (
              <ScrollableTable>
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead>Key</TableHead>
                    <TableHead>Label</TableHead>
                    <TableHead>Kind</TableHead>
                    <TableHead>Source</TableHead>
                    <TableHead>Filter / Formula</TableHead>
                    <TableHead>Format</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {datasource.measures.map((m, _i, pool) => {
                    // Time windows are their own kind, not formulas (D1).
                    const kind = measureKind(m, pool);
                    const isFormula = kind === "formula";
                    return (
                      <TableRow key={m.key}>
                        <TableCell className="font-mono text-sm">{m.key}</TableCell>
                        <TableCell>{m.label}</TableCell>
                        <TableCell>
                          <Badge variant={kind === "column" ? "secondary" : "outline"}>
                            {kind === "window" ? "time window" : kind}
                          </Badge>
                        </TableCell>
                        <TableCell className="text-sm">
                          {m.base ? (
                            <span>
                              window over <span className="font-mono">{m.base}</span>
                            </span>
                          ) : isFormula ? (
                            <span className="text-muted-foreground">—</span>
                          ) : (
                            <span className="font-mono">
                              {(m.aggregation ?? "sum").toUpperCase()}(
                              {m.column})
                            </span>
                          )}
                        </TableCell>
                        <TableCell className="font-mono text-xs max-w-md truncate">
                          {m.base ? "" : isFormula ? m.formula : (m.filter ?? "")}
                        </TableCell>
                        <TableCell className="font-mono text-xs">
                          {m.format ?? "—"}
                        </TableCell>
                      </TableRow>
                    );
                  })}
                </TableBody>
              </Table>
              </ScrollableTable>
            ) : (
              <p className="text-sm text-muted-foreground">
                No datasource-level measures defined. Reports and dashboard
                tiles built on this datasource will only see the measures they
                define themselves. Add a measure here to reuse it everywhere.
              </p>
            )}
          </section>
        </TabsContent>

        <TabsContent value="reports" className="mt-6">
          <section className="rounded-lg border p-6">
            <DatasourceLinkedReports
              datasourceId={datasource.id}
              datasourceSlug={datasource.slug}
              onCountChange={handleCountChange}
            />
          </section>
        </TabsContent>

        {!datasource.isSystem && (
          <TabsContent value="variants" className="mt-6">
            <section className="rounded-lg border p-6">
              <DatasourceVariantsList
                datasourceSlug={datasource.slug}
                parentQuery={datasource.query}
                parentColumns={datasource.columns ?? []}
                isSystemDatasource={datasource.isSystem}
                onCountChange={handleVariantCountChange}
                onParentQueryUpdated={(newQuery) =>
                  setDatasource((prev) =>
                    prev ? { ...prev, query: newQuery } : prev,
                  )
                }
              />
            </section>
          </TabsContent>
        )}

        {!datasource.isSystem && bypassesGroups && (
          <TabsContent value="audit" className="mt-6">
            <section className="rounded-lg border p-6">
              <DatasourceAuditTab datasourceSlug={datasource.slug} />
            </section>
          </TabsContent>
        )}
      </Tabs>
    </div>
  );
}
